import { zodResolver } from "@hookform/resolvers/zod";
import {
  Loader2,
  MailPlus,
  MoreVertical,
  ShieldAlert,
  ShieldCheck,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import type { MemberRole, MemberStatus, TeamMemberResponse } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMeQuery } from "@/features/organizers/api";
import {
  useDeleteMemberMutation,
  useDisableMemberMutation,
  useEnableMemberMutation,
  useInviteMemberMutation,
  useResendInviteMutation,
  useTeamMembersQuery,
  useUpdateMemberRoleMutation,
} from "@/features/team/api";
import { ApiError } from "@/lib/api-client";
import { useConfirm } from "@/lib/confirm";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const INVITABLE_ROLES = ["Manager", "Editor", "Accountant", "Gate"] as const;

const STATUS_VARIANT: Record<MemberStatus, "success" | "warning" | "secondary"> = {
  Active: "success",
  Invited: "warning",
  Disabled: "secondary",
};

export function TeamPage() {
  return (
    <DashboardPage>
      <TeamView />
    </DashboardPage>
  );
}

function TeamView() {
  const me = useMeQuery();
  const members = useTeamMembersQuery();
  const t = useT("team");

  if (me.data && me.data.role !== "Owner") {
    return (
      <Card>
        <CardHeader className="items-center text-center">
          <ShieldAlert className="h-10 w-10 text-amber-500" />
          <CardTitle>{t("forbiddenTitle")}</CardTitle>
          <CardDescription>{t("forbiddenDescription")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (members.isLoading || !me.data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (members.error) {
    return (
      <QueryError
        message={t("loadError")}
        onRetry={() => members.refetch()}
        retrying={members.isFetching}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("description")}</p>
      </div>

      <InviteCard />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("membersTitle")}</CardTitle>
          <CardDescription>
            {t("membersCount", { count: members.data?.length ?? 0 })}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {members.data?.map((member) => (
              <MemberRow key={member.id} member={member} currentMemberId={me.data!.memberId} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const inviteSchema = z.object({
  email: z.string().email("validation.emailInvalid"),
  role: z.enum(INVITABLE_ROLES),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

function InviteCard() {
  const invite = useInviteMemberMutation();
  const t = useT("team.invite");
  const tRole = useT("roles");
  const tHint = useT("roleHints");
  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "", role: "Editor" },
  });

  async function onSubmit(values: InviteFormValues) {
    try {
      await invite.mutateAsync(values);
      toast.success(t("sent", { email: values.email }));
      form.reset();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("failed"));
    }
  }

  const selectedRole = form.watch("role");
  const hint = selectedRole ? tHint(selectedRole) : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">{t("title")}</CardTitle>
        </div>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid gap-4 sm:grid-cols-[1fr,180px,auto] sm:items-end"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("email")}</FormLabel>
                  <FormControl>
                    <Input type="email" dir="ltr" placeholder={t("emailPlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("role")}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("rolePlaceholder")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INVITABLE_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          {tRole(role)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? t("sending") : t("submit")}
            </Button>
          </form>
        </Form>
        {hint ? (
          <p className="mt-3 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MemberRow({
  member,
  currentMemberId,
}: {
  member: TeamMemberResponse;
  currentMemberId: string;
}) {
  const [showActions, setShowActions] = useState(false);
  const updateRole = useUpdateMemberRoleMutation(member.id);
  const disable = useDisableMemberMutation(member.id);
  const enable = useEnableMemberMutation(member.id);
  const del = useDeleteMemberMutation(member.id);
  const resend = useResendInviteMutation(member.id);
  const confirm = useConfirm();
  const tConfirm = useT("confirm.deleteMember");
  const t = useT("team.member");
  const tInvite = useT("team.invite");
  const tRole = useT("roles");
  const tStatus = useT("memberStatus");
  const fmt = useFormat();

  const isOwner = member.role === "Owner";
  const isSelf = member.id === currentMemberId;

  async function handleRoleChange(role: string) {
    if (role === member.role) return;
    try {
      await updateRole.mutateAsync({ role: role as MemberRole });
      toast.success(t("roleUpdated"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("roleUpdateFailed"));
    }
  }

  async function handleDisable() {
    try {
      await disable.mutateAsync();
      toast.success(t("disabled"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("actionFailed"));
    }
  }

  async function handleEnable() {
    try {
      await enable.mutateAsync();
      toast.success(t("enabled"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("actionFailed"));
    }
  }

  async function handleDelete() {
    const ok = await confirm({
      title: tConfirm("title"),
      description: tConfirm("description", { email: member.email }),
      confirmLabel: tConfirm("action"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync();
      toast.success(t("deleted"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("deleteFailed"));
    }
  }

  async function handleResend() {
    try {
      await resend.mutateAsync();
      toast.success(t("resent", { email: member.email }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : tInvite("failed"));
    }
  }

  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{member.email}</span>
          {isSelf ? (
            <Badge variant="secondary" className="text-[10px]">
              {t("you")}
            </Badge>
          ) : null}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant={STATUS_VARIANT[member.status]}>{tStatus(member.status)}</Badge>
          <span>·</span>
          <span>{tRole(member.role)}</span>
          {member.lastLoginAt ? (
            <>
              <span>·</span>
              <span>{t("lastLogin", { date: fmt.date(member.lastLoginAt) })}</span>
            </>
          ) : member.inviteExpiresAt && member.status === "Invited" ? (
            <>
              <span>·</span>
              <span>{t("inviteValidUntil", { date: fmt.date(member.inviteExpiresAt) })}</span>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!isOwner && !isSelf && member.status === "Invited" ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleResend}
            disabled={resend.isPending}
            className="h-9"
          >
            <MailPlus className="me-1.5 h-3.5 w-3.5" />
            {resend.isPending ? tInvite("sending") : t("resend")}
          </Button>
        ) : null}

        {!isOwner && !isSelf ? (
          <Select
            value={member.role}
            onValueChange={handleRoleChange}
            disabled={updateRole.isPending}
          >
            <SelectTrigger className="h-9 w-[160px]" aria-label={t("changeRole")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INVITABLE_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {tRole(role)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}

        {!isOwner && !isSelf ? (
          <div className="relative">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => setShowActions((s) => !s)}
              aria-label={t("moreActions")}
              aria-expanded={showActions}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
            {showActions ? (
              <div
                className="absolute end-0 z-20 mt-1 w-48 rounded-md border bg-popover p-1 shadow-md"
                onMouseLeave={() => setShowActions(false)}
              >
                {member.status === "Disabled" ? (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                    onClick={() => {
                      setShowActions(false);
                      handleEnable();
                    }}
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {t("enable")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                    onClick={() => {
                      setShowActions(false);
                      handleDisable();
                    }}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    {t("disable")}
                  </button>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                  onClick={() => {
                    setShowActions(false);
                    handleDelete();
                  }}
                >
                  {t("delete")}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
